<template>
  <v-row align="center"
         justify="center">
    <v-card max-width="384">
      <v-form ref="form"
              v-model="valid"
              method="post"
              @submit.prevent="onSubmit"
              lazy-validation>
        <v-card-title class="d-flex flex-column">
          <a href="/"
             tabindex="-1"
             class="mt-5">
            <v-img src="/images/logo_blue.svg"
                   height="50"
                   width="300"
                   contain />
          </a>
          <span class="mt-3">Log in to your account</span>
        </v-card-title>
        <v-window v-model="step">
          <v-window-item :value="1">
            <v-card-text>
              <v-text-field label="Login"
                            name="login"
                            v-model="username"
                            type="text"
                            required
                            :rules="rules.required" />
              <v-text-field label="Password"
                            name="password"
                            v-model="password"
                            :type="show ? 'text' : 'password'"
                            required
                            :rules="rules.required"
                            :append-icon="show ? 'mdi-eye' : 'mdi-eye-off'"
                            @click:append="show = !show" />
            </v-card-text>
          </v-window-item>
          <v-window-item :value="2">
            <v-card-text>
              <template v-if="qrcode && qrcode.length != 6">
                <p class="text-center">Scan the code with your Authenticator app and press Next.</p>
                <qr-code class="qrcode"
                         :text="qrcode"></qr-code>
              </template>
            </v-card-text>
          </v-window-item>
          <v-window-item :value="3">
            <v-card-text>
              <v-text-field label="Two-factor authentication code"
                            v-model="otp"
                            autofocus
                            required
                            :rules="[...rules.required, ...rules.minimum]"
                            hint="Enter code from the 2FA app on your mobile device"
                            persistent-hint
                            @keyup.enter="onSubmit" />

            </v-card-text>
          </v-window-item>
        </v-window>
        <v-card-actions class="d-flex flex-column">
          <v-btn v-if="step == 1"
                 depressed
                 block
                 type="submit"
                 color="success"
                 :loading="loading"
                 :disabled="loading || !valid">
            Next
          </v-btn>
          <v-btn v-if="step == 2"
                 depressed
                 block
                 type="submit"
                 color="success"
                 :loading="loading"
                 :disabled="loading">
            Next
          </v-btn>
          <v-btn v-if="step == 3"
                 depressed
                 block
                 type="submit"
                 color="success"
                 :loading="loading"
                 :disabled="loading || !otp || otp.length != 6">
            Login
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-row>
</template>

<script>
import Authentication from "@/api/authentication"
import Users from "@/api/users"
import VueQRCodeComponent from "vue-qrcode-component"
export default {
  name: "Login",
  components: {
    qrCode: VueQRCodeComponent
  },
  data() {
    return {
      step: 1,
      valid: false,
      loading: false,
      username: null,
      password: null,
      qrcode: null,
      otp: null,
      rules: {
        required: [v => !!v || "Field is required"],
        minimum: [v => (!!v && v.length == 6) || "Enter the full code"]
      },
      tokens: null,
      identity: null,
      show: false
    }
  },
  methods: {
    /**
     * Show error message to user
     */
    showError(message, error = null) {
      this.loading = false
      this.$store.commit("showSnackbar", {
        text: message,
        color: "error"
      })
    },

    /**
     * Reset login process back to step 1
     */
    resetLogin() {
      this.step = 1
      this.loading = false
      this.otp = null
      this.qrcode = null
      this.tokens = null
      this.identity = null
      this.valid = true
      this.$store.commit("authLogout")
    },

    /**
     * Perform login procedure
     * 1. Authenticate user with credentials; server returns tokens
     * 2. Save tokens; request and store user data using tokens
     * 3. If 2FA is disabled, route to home page, else perform 2FA
     */
    async onSubmit() {
      if (!this.$refs.form.validate()) {
        this.showError("Please fill in all required fields")
        return
      }

      this.loading = true

      try {
        if (this.step == 1) {
          // Step 1: Initial authentication
          try {
            const loginResponse = await Authentication.token({
              username: this.username,
              password: this.password
            })

            this.tokens = loginResponse.data
            this.$store.commit("authLogin", {
              access: this.tokens.access,
              refresh: this.tokens.refresh
            })

            // Get user data
            try {
              const userResponse = await Users.retrieve(this.username)
              this.identity = userResponse.data

              if (!this.identity.two_factor) {
                // No 2FA required, complete login
                this.$store.commit("setUser", { user: this.identity })
                this.$router.push({ name: "home" })
              } else {
                // 2FA required
                if (!this.identity.secret_key) {
                  // Generate new 2FA secret
                  try {
                    const otpResponse = await Authentication.otp(this.username)
                    this.qrcode = otpResponse.data
                    this.step = 2
                    this.loading = false
                  } catch (otpError) {
                    this.showError("Failed to generate two-factor authentication code", otpError)
                  }
                } else {
                  // Use existing 2FA
                  this.step = 3
                  this.loading = false
                }
              }
            } catch (userError) {
              this.showError("Failed to retrieve user information", userError)
            }
          } catch (authError) {
            // Handle authentication errors
            if (authError.response && authError.response.status === 401) {
              this.showError("Invalid username or password")
            } else if (authError.response && authError.response.status === 400) {
              this.showError("Please check your login credentials")
            } else if (authError.response && authError.response.data && authError.response.data.detail) {
              this.showError(authError.response.data.detail)
            } else {
              this.showError("Login failed. Please try again later.")
            }
          }
        } else if (this.step == 2) {
          // Step 2: QR code shown, move to OTP entry
          this.step++
          this.loading = false
        } else if (this.step == 3) {
          // Step 3: OTP verification
          if (!this.otp || this.otp.length !== 6) {
            this.showError("Please enter a valid 6-digit authentication code")
            return
          }

          try {
            const otpResponse = await Authentication.login(this.username, { otp: this.otp })
            if (JSON.parse(otpResponse.data)) {
              this.$store.commit("setUser", { user: this.identity })
              this.$router.push({ name: "home" })
            } else {
              this.showError("Invalid authentication code. Please try again.")
            }
          } catch (otpError) {
            if (otpError.response && otpError.response.status === 400) {
              this.showError("Invalid authentication code")
            } else {
              this.showError("Two-factor authentication failed. Please try again.")
            }
          }
        }
      } catch (error) {
        this.showError("An unexpected error occurred. Please try again.", error)
      }
    }
  }
}
</script>
